<template>
  <div class="admin-users">
    <div class="content">
      <el-card>
        <template #header>
          <div class="header-bar">
            <span class="title">用户管理</span>
            <template v-if="!isMobile">
              <el-input
                v-model="searchUsername"
                placeholder="按用户名搜索"
                size="small"
                clearable
                class="header-search"
              />
              <el-button
                type="primary"
                size="small"
                class="header-add-btn"
                @click="openAddUserDialog"
              >新增用户</el-button>
            </template>
          </div>
        </template>
        
        <!-- 桌面和平板端显示 -->
        <div class="table-responsive desktop-table" v-if="!isMobile">
          <el-table :data="users" style="width: 100%" v-loading="loading">
            <el-table-column label="序号" width="70" align="center">
              <template #default="scope">
                {{ scope.$index + 1 }}
              </template>
            </el-table-column>
            <el-table-column prop="username" label="用户名" width="150"></el-table-column>
            <el-table-column prop="category" label="用户分类" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="role" label="角色" min-width="100" align="center" >
              <template #default="scope">
                {{ scope.row.role === 'ADMIN' ? '管理员' : '普通用户' }}
              </template>
            </el-table-column>
            <el-table-column prop="purchasingUnits" label="采购单位" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column prop="status" label="状态" width="90" align="center">
              <template #default="scope">
                <el-tag :type="getStatusType(scope.row.status)">
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="createAt" label="注册时间" min-width="160">
              <template #default="scope">
                {{ formatDate(scope.row.createAt) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="180" align="center" fixed="right">
              <template #default="scope">
                <div class="action-buttons">
                  <el-button
                      size="small"
                      type="primary"
                      @click="openEditUserDialog(scope.row)">
                    修改
                  </el-button>
                  <el-button
                      size="small"
                      :type="scope.row.status === 'ACTIVE' ? 'warning' : 'success'"
                      @click="handleToggleStatus(scope.row)">
                    {{ scope.row.status === 'ACTIVE' ? '禁用' : '启用' }}
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
        
        <!-- 手机端卡片式布局 -->
        <div class="mobile-cards" v-else>
          <!-- 移动端新增用户按钮 -->
          <el-button
            type="primary"
            size="small"
            style="width: 100%; margin-bottom: 10px;"
            @click="openAddUserDialog"
          >新增用户</el-button>
          <el-input
              v-model="searchUsername"
              placeholder="按用户名搜索"
              size="default"
              clearable
              style="margin-bottom: 10px"
          />
          <div v-if="loading" class="loading-container">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>加载中...</span>
          </div>
          <div v-else>
            <div class="user-card" v-for="user in users" :key="user.userId">
              <div class="user-info">
                <div class="user-header">
                  <span class="username">{{ user.username }}</span>
                  <el-tag size="small" :type="getStatusType(user.status)">
                    {{ getStatusText(user.status) }}
                  </el-tag>
                </div>
                <div class="user-details">
                  <div class="detail-item">
                    <span class="label">序号:</span>
                    <span class="value">{{ users.indexOf(user) + 1 }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">角色:</span>
                    <span class="value">{{ user.role === 'ADMIN' ? '管理员' : '普通用户' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">用户分类:</span>
                    <span class="value">{{ user.category || '-' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">采购单位:</span>
                    <span class="value">{{ user.purchasingUnits }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">注册时间:</span>
                    <span class="value">{{ formatDate(user.createAt) }}</span>
                  </div>
                </div>
              </div>
              <div class="user-actions">
                <el-button
                  size="small"
                  type="primary"
                  @click="openEditUserDialog(user)">
                  修改
                </el-button>
                <el-button
                  size="small"
                  :type="user.status === 'ACTIVE' ? 'warning' : 'success'"
                  @click="handleToggleStatus(user)">
                  {{ user.status === 'ACTIVE' ? '禁用' : '启用' }}
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>

      <!--分页功能-->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10,20,50,100]"
        :page-size="pageSize"
        layout="total,size,->,prev,pager,next,jumper"
        :total="totalItems"
        background
        ></el-pagination>

      <!-- 新增用户对话框 -->
      <el-dialog v-model="addDialogVisible" title="新增用户" width="400px">
        <el-form :model="addUserForm" :rules="addUserRules" ref="addUserFormRef" label-width="80px">
          <el-form-item label="用户名" prop="username">
            <el-input v-model="addUserForm.username" placeholder="请输入用户名" />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input v-model="addUserForm.password" placeholder="请输入密码" type="password" />
          </el-form-item>
          <el-form-item label="采购单位" prop="purchasingUnits">
            <el-input v-model="addUserForm.purchasingUnits" placeholder="请输入采购单位" />
          </el-form-item>
          <el-form-item label="用户分类" prop="category">
            <el-select
              v-model="addUserForm.category"
              placeholder="请选择或输入用户分类"
              filterable
              allow-create
              default-first-option
              :reserve-keyword="false"
              clearable
              style="width: 100%;"
            >
              <el-option
                v-for="item in categoryOptions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleAddUser">确定</el-button>
        </template>
      </el-dialog>

      <!-- 修改用户对话框 -->
      <el-dialog v-model="editDialogVisible" title="修改用户" width="400px" class="edit-user-dialog">
        <el-form :model="editUserForm" :rules="editUserRules" ref="editUserFormRef" label-width="80px" class="edit-user-form">
          <el-form-item label="用户名">
            <el-input v-model="editUserForm.username" disabled />
          </el-form-item>
          <el-form-item label="新密码" prop="password">
            <el-input v-model="editUserForm.password" placeholder="如需修改请输入新密码" type="password" />
          </el-form-item>
          <el-form-item label="采购单位" prop="purchasingUnits">
            <el-input v-model="editUserForm.purchasingUnits" placeholder="请输入采购单位" />
          </el-form-item>
          <el-form-item label="用户分类" prop="category">
            <el-select
              v-model="editUserForm.category"
              placeholder="请选择或输入用户分类"
              filterable
              allow-create
              default-first-option
              :reserve-keyword="false"
              clearable
              style="width: 100%;"
            >
              <el-option
                v-for="item in categoryOptions"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleEditUser">确定</el-button>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { Loading } from '@element-plus/icons-vue';
import axios from 'axios';
import {useMobileDetection} from "@/utils/mobileAdapter";

const users = ref([]);
const loading = ref(false);
const pageSize = ref(10);
const currentPage = ref(1);
const totalItems = ref(0);
const searchUsername = ref('');
const { isMobile } = useMobileDetection(768);


const fetchUsers = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/api/v1/admin/user', {
      params: {
        pageNum: currentPage.value,
        pageSize: pageSize.value,
        username: searchUsername.value.trim(),
      },
    });
    if (response.data.code === '200') {
      users.value = response.data.data || [];
      totalItems.value = response.data.totalRecords ?? users.value.length;
      currentPage.value = response.data.pageNum ?? currentPage.value;
      pageSize.value = response.data.pageSize ?? pageSize.value;
    } else {
      ElMessage.error(response.data.info);
    }
  } catch (error) {
    ElMessage.error('获取用户列表失败');
  } finally {
    loading.value = false;
  }
};

// 监听输入，实现防抖搜索
let searchTimer = null;
watch(searchUsername, () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchUsers();
  }, 500);
});

const handleSizeChange = (val) => {
  pageSize.value = val;
  currentPage.value = 1;
  fetchUsers();
}
const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchUsers();
}

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  try {
    // 处理字符串格式的日期 "yyyy-MM-dd HH:mm:ss"
    let date;
    if (typeof timestamp === 'string') {
      // 替换空格为 'T' 以符合 ISO 8601 格式，提高兼容性
      const isoString = timestamp.replace(' ', 'T');
      date = new Date(isoString);
      
      // 如果解析失败，尝试直接解析原字符串
      if (isNaN(date.getTime())) {
        date = new Date(timestamp);
      }
    } else {
      date = new Date(timestamp);
    }
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', timestamp);
      return '-';
    }
    
    // 使用更详细的格式化选项，确保跨平台兼容性
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Date formatting error:', error, timestamp);
    return '-';
  }
};

const getStatusType = (status) => {
  switch (status) {
    case 'ACTIVE': return 'success';
    case 'DISABLE': return 'danger';
    default: return 'info';
  }
};

const getStatusText = (status) => {
  switch (status) {
    case 'ACTIVE': return '启用';
    case 'DISABLE': return '禁用';
    default: return status;
  }
};


const handleToggleStatus = async (row) => {
  try {
    const newStatus = row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const response = await axios.put(`/api/v1/admin/${newStatus === 'INACTIVE' ? 'disable' : 'enable'}?userId=${row.userId}`);

    if (response.data.code === '200') {
      ElMessage.success('状态更新成功');
      fetchUsers();
    } else {
      ElMessage.error(response.data.info);
    }
  } catch (error) {
    ElMessage.error('状态更新失败');
  }
};

onMounted(() => {
  fetchUsers();
});

// 添加样式
const style = document.createElement('style');
style.textContent = `
.edit-user-dialog {
  width: 400px !important;
}

.edit-user-dialog .el-message-box__content {
  padding: 20px;
}

.edit-user-form .form-item {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.edit-user-form .form-item label {
  width: 80px;
  text-align: right;
  padding-right: 12px;
  color: #606266;
}

.edit-user-form .form-item .el-input__inner {
  flex: 1;
  height: 32px;
  line-height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  background-color: #fff;
}

.edit-user-form .form-item .el-input__inner:focus {
  border-color: #409eff;
  outline: none;
}

@media screen and (max-width: 768px) {
  .edit-user-dialog {
    width: 90% !important;
    max-width: 400px;
  }
  
  .edit-user-dialog .el-message-box__content {
    padding: 15px;
  }
  
  .edit-user-form .form-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .edit-user-form .form-item label {
    width: 100%;
    text-align: left;
    margin-bottom: 5px;
  }
  
  .edit-user-form .form-item .el-input__inner {
    width: 100%;
  }
}
`;
document.head.appendChild(style);

// 新增用户对话框相关
const addDialogVisible = ref(false);
const addUserFormRef = ref(null);
const addUserForm = reactive({
  username: '',
  password: '',
  purchasingUnits: '',
  category: ''
});

const addUserRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ],
  purchasingUnits: [
    { required: true, message: '请输入采购单位', trigger: 'blur' }
  ],
  category: [
    { required: true, message: '请输入用户分类', trigger: 'blur' }
  ]
};

// 分类选项
const categoryOptions = [
  '零售',
  '对公',
  '清阅',
  '卓正',
  '明达'
];

const openAddUserDialog = () => {
  addUserForm.username = '';
  addUserForm.password = '';
  addUserForm.purchasingUnits = '';
  addUserForm.category = '';
  addDialogVisible.value = true;
};

const handleAddUser = () => {
  addUserFormRef.value.validate(async (valid) => {
    if (!valid) return;
    try {
      const response = await axios.post('/api/v1/admin/user', addUserForm);
      if (response.data.code === '200') {
        ElMessage.success('新增用户成功');
        addDialogVisible.value = false;
        fetchUsers();
      } else {
        ElMessage.error(response.data.info || '新增用户失败');
      }
    } catch (error) {
      ElMessage.error('新增用户失败');
    }
  });
};

// 修改用户对话框相关
const editDialogVisible = ref(false);
const editUserFormRef = ref(null);
const editUserForm = reactive({
  userId: null,
  username: '',
  password: '',
  originPassword: '',
  purchasingUnits: '',
  category: '',
  role: '',
  status: ''
});

const editUserRules = {
  purchasingUnits: [
    { required: true, message: '请输入采购单位', trigger: 'blur' }
  ]
};

const openEditUserDialog = (user) => {
  editUserForm.userId = user.userId;
  editUserForm.username = user.username;
  editUserForm.password = '';
  editUserForm.originPassword = user.password || '';
  editUserForm.purchasingUnits = user.purchasingUnits || '';
  editUserForm.category = user.category || '';
  editUserForm.role = user.role || '';
  editUserForm.status = user.status || '';
  editDialogVisible.value = true;
};

const handleEditUser = () => {
  editUserFormRef.value.validate(async (valid) => {
    if (!valid) return;
    try {
      const payload = {
        userId: editUserForm.userId,
        username: editUserForm.username,
        purchasingUnits: editUserForm.purchasingUnits,
        category: editUserForm.category,
        role: editUserForm.role,
        status: editUserForm.status,
        password: editUserForm.password ? editUserForm.password : editUserForm.originPassword
      };
      const response = await axios.put('/api/v1/admin/user', payload);
      if (response.data.code === '200') {
        ElMessage.success('修改成功');
        editDialogVisible.value = false;
        fetchUsers();
      } else {
        ElMessage.error(response.data.info || '修改失败');
      }
    } catch (error) {
      ElMessage.error('修改失败');
    }
  });
};
</script>

<style scoped>

.admin-users {
  padding: 20px;
}

.content {
  max-width: 1200px;
  margin: 0 auto;
}


.table-responsive {
  width: 100%;
  overflow-x: auto;
}


.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  align-items: center;
}


/* 手机端卡片式布局 */
.mobile-cards {
  width: 100%;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  font-size: 14px;
  color: #909399;
}

.loading-container .el-icon {
  margin-right: 5px;
  font-size: 18px;
}

.user-card {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 10px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.user-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #EBEEF5;
}

.username {
  font-weight: bold;
  font-size: 16px;
}

.user-details {
  margin-bottom: 12px;
}

.detail-item {
  display: flex;
  margin-bottom: 5px;
}

.detail-item .label {
  width: 80px;
  color: #909399;
  font-size: 13px;
}

.detail-item .value {
  flex: 1;
  color: #303133;
  font-size: 13px;
}

.user-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.user-actions .el-button {
  margin: 0;
  flex: 1;
}

.header-bar {
  display: flex;
  align-items: center;
}

.header-bar .title {
  font-size: 16px;
  font-weight: 600;
}

.header-search {
  margin-left: 10px;
  width: 180px;
}

.header-add-btn {
  margin-left: 10px;
}

@media screen and (max-width: 768px) {
  .admin-users {
    padding: 10px;
  }

  :deep(.el-pagination__jump) {
    display: none !important;
  }
  :deep(.el-pagination__total){
    display: none !important;
  }
  :deep(.el-pagination__rightwrapper){
    justify-content: center;
  }
  
  .el-card {
    margin-bottom: 15px;
  }
  
  .el-card :deep(.el-card__header) {
    padding: 12px 15px;
  }
  
  .el-card :deep(.el-card__body) {
    padding: 12px;
  }
  
  .el-button {
    margin-right: 5px;
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .action-buttons {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-buttons .el-button {
    margin: 3px 0;
    width: 100%;
  }
}

@media screen and (max-width: 480px) {
  .admin-users {
    padding: 8px;
  }
  
  .el-card {
    margin-bottom: 10px;
  }
  
  .el-card :deep(.el-card__header) {
    padding: 10px;
    font-size: 16px;
  }
  
  .el-card :deep(.el-card__body) {
    padding: 8px;
  }
  
  .user-card {
    padding: 10px;
    margin-bottom: 8px;
  }
  
  .user-header {
    margin-bottom: 8px;
    padding-bottom: 8px;
  }
  
  .username {
    font-size: 15px;
  }
  
  .detail-item .label,
  .detail-item .value {
    font-size: 12px;
  }
  
  .user-actions .el-button {
    font-size: 12px;
    padding: 5px 8px;
    height: 28px;
  }
}

@media screen and (max-width: 320px) {
  .admin-users {
    padding: 5px;
  }
  
  .el-card :deep(.el-card__header) {
    padding: 8px;
    font-size: 15px;
  }
  
  .el-card :deep(.el-card__body) {
    padding: 5px;
  }
  
  .user-card {
    padding: 8px;
  }
  
  .username {
    font-size: 14px;
  }
  
  .detail-item .label {
    width: 70px;
    font-size: 11px;
  }
  
  .detail-item .value {
    font-size: 11px;
  }
  
  .user-actions {
    flex-direction: column;
    gap: 5px;
  }
  
  .user-actions .el-button {
    width: 100%;
    margin: 0;
    padding: 4px 6px;
    font-size: 11px;
    height: 26px;
  }
}
</style> 